import { useEffect } from "react";
import Animated, {
  useAnimatedStyle,
  useSharedValue,
  withRepeat,
  withTiming,
  Easing,
} from "react-native-reanimated";
import Svg, { Circle, G, Path, Rect } from "react-native-svg";

// The illustration palette, not the UI palette. `colors` carries a set built
// for warm anatomical drawing — the same family the app's biology figures use
// — which `EMBER.colors` does not re-export.
import { colors } from "@louise/moon-ui/theme/tokens";
import type { OverlayFigure } from "../../overlay/figures";
import { withAlpha } from "@louise/theme/tokens";

/**
 * The step's diagram, drawn in the card that carries the step.
 *
 * It was drawn over the camera, and the header here still said so long after
 * `InjectionZones` took that job: what goes over the picture is an outline of
 * her own body, not a drawing of somebody's. This renders in the card instead,
 * where every step has one and the camera may not be on at all.
 *
 * **It has to read on what it sits on**, which is why a tone is required and
 * why there is no default. The warm cream card takes a dark drawing; a live
 * camera feed — a bathroom, a kitchen, any colour at any brightness — takes a
 * light one on a dark panel. Getting that backwards produces a cream figure on
 * a cream card, invisible rather than merely ugly.
 *
 * **It must not be mirrored.** Nothing here is flipped, and nothing here
 * depicts a side, so the same drawing is right whichever way the preview beside
 * it faces. `InjectionCoachCameraStage` is where the separation between a
 * mirrored picture and an unmirrored drawing is enforced.
 *
 * The figures depict what the step's words already say and nothing more — the
 * right angle the injection step names is drawn, the distance from the navel is
 * shown as an offset without a number, because the number is localised prose
 * and a diagram that carried one would quietly become the instruction. See
 * `overlay/figures.ts`.
 */

/**
 * Where the diagram is being drawn, which decides what colour it can be.
 *
 * The figures are the same lines either way. Only the palette changes, because
 * the two backgrounds are opposites: a live camera feed is arbitrary and often
 * dark, so the drawing is light on a dark panel; the step card is a fixed warm
 * cream, so the same drawing has to be dark on almost nothing. Rendering the
 * video palette on the card produces a cream figure on a cream card, which is
 * invisible rather than merely ugly.
 */
export type OverlayTone = "on-video" | "on-card";

type Palette = {
  stroke: string;
  accent: string;
  muted: string;
  panel: string;
  border: string;
};

const PALETTES: Readonly<Record<OverlayTone, Palette>> = {
  "on-video": {
    stroke: colors.biologyCream,
    accent: colors.biologyAmber,
    muted: colors.biologyFrost,
    // Dark enough to carry a light drawing over a bright bathroom.
    panel: "rgba(28,18,14,0.52)",
    border: withAlpha(colors.biologyCream, 0.18),
  },
  "on-card": {
    stroke: colors.biologyBrown,
    accent: colors.biologyCopper,
    muted: colors.greyBrown,
    // Barely there: the card already separates this from the copy, and a second
    // filled box inside it reads as a different kind of content.
    panel: withAlpha(colors.biologyCopper, 0.06),
    border: withAlpha(colors.biologyBrown, 0.1),
  },
};

const SIZE = 132;

export function StepOverlay({
  figure,
  tone,
}: {
  figure: OverlayFigure;
  /**
   * Required, with no default. It defaulted to `on-video`, which is the palette
   * nothing renders any more — so the next caller who left it off would have
   * got a cream figure on a cream card, which this file's own note above calls
   * invisible rather than merely ugly.
   */
  tone: OverlayTone;
}) {
  const palette = PALETTES[tone];

  // One slow breath, on the panel rather than on the SVG attributes. Animating
  // the container is the same effect and avoids the animated-SVG-prop path,
  // which is the part that behaves differently across platforms — and this has
  // to be right in a browser and on two phones.
  const pulse = useSharedValue(0);
  useEffect(() => {
    pulse.value = withRepeat(
      withTiming(1, { duration: 2200, easing: Easing.inOut(Easing.ease) }),
      -1,
      true,
    );
  }, [pulse]);

  const breathing = useAnimatedStyle(() => ({
    opacity: 0.86 + pulse.value * 0.14,
    transform: [{ scale: 0.985 + pulse.value * 0.015 }],
  }));

  return (
    <Animated.View
      // Decorative: the step's own copy is the instruction, and it is rendered
      // as text directly beneath this. Announcing the drawing as well would read
      // the same step twice to anyone using a screen reader.
      accessible={false}
      importantForAccessibility="no-hide-descendants"
      pointerEvents="none"
      style={[
        {
          width: SIZE + 26,
          height: SIZE + 26,
          borderRadius: 26,
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: palette.panel,
          borderWidth: 1,
          borderColor: palette.border,
        },
        breathing,
      ]}
    >
      <Svg width={SIZE} height={SIZE} viewBox="0 0 100 100">
        <Figure figure={figure} p={palette} />
      </Svg>
    </Animated.View>
  );
}

function Figure({ figure, p }: { figure: OverlayFigure; p: Palette }) {
  if (figure === "timing") return <TimingFigure p={p} />;
  if (figure === "hygiene") return <HygieneFigure p={p} />;
  if (figure === "device") return <DeviceFigure p={p} />;
  if (figure === "mix") return <MixFigure p={p} />;
  if (figure === "gluteal-site") return <GlutealSiteFigure p={p} />;
  if (figure === "aspiration-inject") return <AspirationInjectFigure p={p} />;
  if (figure === "site") return <SiteFigure p={p} />;
  if (figure === "inject") return <InjectFigure p={p} />;
  if (figure === "inject-straight") {
    return <InjectFigure p={p} angle="none" />;
  }
  if (figure === "inject-angled") {
    return <InjectFigure p={p} angle="acute" />;
  }
  return <AftercareFigure p={p} />;
}

/** An alarm bell: no dial or hands that could prescribe a time. */
export function TimingFigure({ p }: { p: Palette }) {
  return (
    <G
      fill="none"
      stroke={p.stroke}
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <Path d="M30 59 V43 C30 31 38 23 50 23 C62 23 70 31 70 43 V59 L77 69 H23 Z" />
      <Path d="M44 78 C46 84 54 84 56 78" stroke={p.accent} strokeWidth="3" />
      <Path d="M46 22 V19 C46 14 54 14 54 19 V22" stroke={p.muted} />
      <Path
        d="M20 32 C16 38 15 44 15 50 M80 32 C84 38 85 44 85 50"
        stroke={p.accent}
      />
      <Path d="M39 41 C39 36 42 32 46 31" stroke={p.muted} strokeWidth="2" />
    </G>
  );
}

/** Two cupped hands beneath running water, without an identifiable arm. */
export function HygieneFigure({ p }: { p: Palette }) {
  return (
    <G
      fill="none"
      stroke={p.stroke}
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <Path d="M15 66 L27 79 C32 83 39 83 44 79 L50 74 L56 79 C61 83 68 83 73 79 L85 66" />
      <Path d="M15 53 L29 64 L40 64 C46 64 46 70 41 71 L31 71 M85 53 L71 64 L60 64 C54 64 54 70 59 71 L69 71" />
      <Path
        d="M22 58 L29 55 L39 58 M78 58 L71 55 L61 58"
        stroke={p.muted}
        strokeWidth="2"
      />
      <Path d="M35 20 V31 M50 16 V29 M65 20 V31" stroke={p.muted} />
      <Path
        d="M35 38 C35 38 31 44 31 46 C31 51 39 51 39 46 C39 44 35 38 35 38 Z M65 38 C65 38 61 44 61 46 C61 51 69 51 69 46 C69 44 65 38 65 38 Z"
        stroke={p.accent}
      />
      <Path d="M50 37 V45" stroke={p.accent} strokeWidth="3" />
    </G>
  );
}

/** An unmarked barrel and a detached cap; no dose control or priming drop. */
export function DeviceFigure({ p }: { p: Palette }) {
  return (
    <G
      fill="none"
      stroke={p.stroke}
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <Rect x="16" y="39" width="43" height="22" rx="6" />
      <Path d="M25 40 V60" stroke={p.muted} strokeWidth="2" />
      <Path d="M59 45 H64 V55 H59 M64 50 H73" />
      <Path
        d="M80 41 H84 C87 41 89 43 89 46 V54 C89 57 87 59 84 59 H80 Z"
        stroke={p.accent}
        strokeWidth="3"
      />
      <Path d="M34 46 H49" stroke={p.muted} strokeWidth="2" />
    </G>
  );
}

/** A stoppered vial and syringe share a transfer arrow, without a dose or a prescribed transfer direction. */
export function MixFigure({ p }: { p: Palette }) {
  return (
    <G
      fill="none"
      stroke={p.stroke}
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <Path d="M31 52 V57 C31 61 25 62 25 67 V80 C25 84 28 86 32 86 H50 C54 86 57 84 57 80 V67 C57 62 51 61 51 57 V52" />
      <Rect x="29" y="46" width="24" height="6" rx="2" />
      <Path d="M34 70 V78" stroke={p.muted} strokeWidth="2" />
      <Path d="M56 26 L64 34 L53 45 Q51 47 49 45 L45 41 Q43 39 45 37 Z M53 23 L67 37 M60 30 L69 21 M65 17 L73 25 M47 43 L40 50" />
      <Path
        d="M70 53 C83 58 80 74 66 75 M70 53 L69 60 M70 53 L77 51 M66 75 L72 69 M66 75 L73 80"
        stroke={p.accent}
        strokeWidth="3"
      />
    </G>
  );
}

/** Symmetric buttocks mark both upper outer quadrants as alternatives, without a needle or a preferred side. */
export function GlutealSiteFigure({ p }: { p: Palette }) {
  return (
    <G
      fill="none"
      stroke={p.stroke}
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <Path d="M25 18 C26 28 18 34 16 47 C13 61 16 76 27 81 C36 85 46 82 50 74 C54 82 64 85 73 81 C84 76 87 61 84 47 C82 34 74 28 75 18" />
      <Path d="M50 39 V74" />
      <Path
        d="M19 57 H46 M54 57 H81 M34 34 V77 M66 34 V77"
        stroke={p.muted}
        strokeWidth="2"
        strokeDasharray="3 5"
      />
      <Circle cx="26" cy="45" r="6" stroke={p.accent} strokeWidth="3" />
      <Circle cx="74" cy="45" r="6" stroke={p.accent} strokeWidth="3" />
    </G>
  );
}

/** A raised plunger and axial pull arrow sit above flat skin, without a pinch, a depth scale or a timing cue. */
export function AspirationInjectFigure({ p }: { p: Palette }) {
  return (
    <G
      fill="none"
      stroke={p.stroke}
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <Path d="M16 72 H84" />
      <Path d="M16 85 H84" stroke={p.muted} strokeWidth="2" />
      <Rect x="43" y="39" width="14" height="22" rx="3" />
      <Path d="M39 39 H61 M44 31 H56 M50 31 V47" />
      <Path d="M44 48 H56" stroke={p.muted} strokeWidth="2" />
      <Path d="M50 61 V81" />
      <Path
        d="M50 24 V13 M45 18 L50 13 L55 18 M50 62 H60 V72"
        stroke={p.accent}
        strokeWidth="3"
      />
    </G>
  );
}

/** A centred lower-abdominal spot, separated from the navel without a side. */
export function SiteFigure({ p }: { p: Palette }) {
  return (
    <G
      fill="none"
      stroke={p.stroke}
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <Path d="M25 16 C26 27 22 34 20 43 C16 56 18 70 24 84 M75 16 C74 27 78 34 80 43 C84 56 82 70 76 84" />
      <Path d="M27 82 C39 88 61 88 73 82" stroke={p.muted} strokeWidth="2" />
      <Circle cx="50" cy="40" r="3" stroke={p.muted} />
      <Path
        d="M50 49 V56"
        stroke={p.muted}
        strokeWidth="2"
        strokeDasharray="2 5"
      />
      <Circle cx="50" cy="69" r="8" stroke={p.accent} strokeWidth="3" />
      <Circle cx="50" cy="69" r="2" fill={p.accent} stroke={p.accent} />
    </G>
  );
}

/**
 * Opposing fingertips lift a fold; the square marks needle against skin.
 *
 * The square is a right-angle mark and is drawn only for a step whose copy
 * names one. A label that says "straight in" and no degrees gets the needle
 * without the mark.
 */
/**
 * The pinch and the needle. `angle` is what the step's copy states: a right
 * angle draws the square, "acute" leans the needle 45° and draws the arc,
 * "none" draws the needle alone.
 */
export function InjectFigure({
  p,
  angle = "right",
}: {
  p: Palette;
  angle?: "right" | "none" | "acute";
}) {
  if (angle === "acute") {
    return (
      <G
        fill="none"
        stroke={p.stroke}
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <Path
          d="M14 80 H25 C32 80 33 58 42 58 H58 C67 58 68 80 75 80 H86"
          strokeWidth="3"
        />
        <Path
          d="M13 58 L25 51 C29 49 33 51 34 55 C35 59 31 63 28 65 L19 70 M87 58 L75 51 C71 49 67 51 66 55 C65 59 69 63 72 65 L81 70"
          stroke={p.muted}
        />
        {/* The syringe, leaning 45° to the right of the needle's entry. */}
        <G transform="rotate(45 50 65)">
          <Rect x="44" y="15" width="12" height="27" rx="3" />
          <Path d="M45 34 H55" stroke={p.muted} strokeWidth="2" />
          <Path d="M50 42 V65" stroke={p.accent} strokeWidth="3" />
        </G>
        {/* The arc between the skin and the needle: the 45° the copy names. */}
        <Path
          d="M64 65 A14 14 0 0 0 59.9 55.1"
          stroke={p.accent}
          strokeWidth="2"
        />
      </G>
    );
  }
  return (
    <G
      fill="none"
      stroke={p.stroke}
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <Path
        d="M14 80 H25 C32 80 33 58 42 58 H58 C67 58 68 80 75 80 H86"
        strokeWidth="3"
      />
      <Path
        d="M13 58 L25 51 C29 49 33 51 34 55 C35 59 31 63 28 65 L19 70 M87 58 L75 51 C71 49 67 51 66 55 C65 59 69 63 72 65 L81 70"
        stroke={p.muted}
      />
      <Rect x="44" y="15" width="12" height="27" rx="3" />
      <Path d="M45 34 H55" stroke={p.muted} strokeWidth="2" />
      <Path d="M50 42 V65" stroke={p.accent} strokeWidth="3" />
      {angle === "right" ? (
        <Path d="M50 48 H60 V58" stroke={p.accent} strokeWidth="2" />
      ) : null}
    </G>
  );
}

/** Downward pressure onto gauze, beside a device entering a sharps container. */
export function AftercareFigure({ p }: { p: Palette }) {
  return (
    <G
      fill="none"
      stroke={p.stroke}
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <Rect x="13" y="51" width="32" height="20" rx="4" />
      <Path
        d="M22 57 V65 M29 57 V65 M36 57 V65 M20 61 H38"
        stroke={p.muted}
        strokeWidth="2"
      />
      <Path d="M12 78 C22 81 36 81 46 78" stroke={p.muted} />
      <Path
        d="M29 23 V42 M23 36 L29 42 L35 36"
        stroke={p.accent}
        strokeWidth="3"
      />
      <Path d="M60 57 L63 82 H83 L86 57 M58 51 H88 V57 H58 Z" />
      <Path d="M67 67 H79" stroke={p.muted} strokeWidth="2" />
      <Rect x="69" y="18" width="8" height="20" rx="2" />
      <Path d="M73 38 V46" stroke={p.accent} />
    </G>
  );
}
